import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Wallet as WalletIcon, Plus, Settings2, Search, Check, X, RefreshCw, Loader2, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inr = (n) => `\u20b9${Number(n || 0).toLocaleString('en-IN')}`;
const statusStyle = (s) => ({
  Approved: 'bg-blue-100 text-blue-700', Paid: 'bg-green-100 text-green-700',
  Rejected: 'bg-red-100 text-red-700', Pending: 'bg-amber-100 text-amber-700',
}[s] || 'bg-slate-100 text-slate-600');

const WalletManagement = () => {
  const [view, setView] = useState('wallets'); // wallets | withdrawals
  const [wallets, setWallets] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [wStatus, setWStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  // modals
  const [creditFor, setCreditFor] = useState(null);
  const [creditAmt, setCreditAmt] = useState('');
  const [creditNote, setCreditNote] = useState('');
  const [creditType, setCreditType] = useState('credit');
  const [minFor, setMinFor] = useState(null);
  const [minAmt, setMinAmt] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchWallets = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/admin/wallets`); setWallets(res.data || []); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  const fetchWithdrawals = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/admin/withdrawals`, { params: wStatus !== 'all' ? { status: wStatus } : {} }); setWithdrawals(res.data || []); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, [wStatus]);

  useEffect(() => { if (view === 'wallets') fetchWallets(); else fetchWithdrawals(); }, [view, fetchWallets, fetchWithdrawals]);

  const submitCredit = async () => {
    const amt = Number(creditAmt);
    if (!amt || amt <= 0) return toast({ title: 'Enter a valid amount', variant: 'destructive' });
    setBusy(true);
    try {
      await axios.post(`${API}/admin/wallet/${creditFor.userId}/transaction`, { amount: amt, type: creditType, note: creditNote });
      toast({ title: `${creditType === 'credit' ? 'Credited' : 'Debited'} ${inr(amt)}` });
      setCreditFor(null); setCreditAmt(''); setCreditNote(''); setCreditType('credit');
      fetchWallets();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const submitMin = async () => {
    const amt = Number(minAmt);
    if (amt < 0 || Number.isNaN(amt)) return toast({ title: 'Enter a valid amount', variant: 'destructive' });
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/wallet/${minFor.userId}/settings`, { minWithdrawal: amt });
      toast({ title: `Min withdrawal set to ${inr(amt)}` });
      setMinFor(null); setMinAmt(''); fetchWallets();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const updateWithdrawal = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/withdrawals/${id}`, { status });
      toast({ title: `Marked ${status}` });
      fetchWithdrawals();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' }); }
  };

  const filteredWallets = wallets.filter((w) =>
    !search || (w.name || '').toLowerCase().includes(search.toLowerCase()) || (w.email || '').toLowerCase().includes(search.toLowerCase()) || String(w.clientId || '').includes(search));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><WalletIcon className="w-6 h-6 text-blue-600" /> Wallet Management</h2>
        <Button variant="outline" onClick={() => view === 'wallets' ? fetchWallets() : fetchWithdrawals()}><RefreshCw className="w-4 h-4 mr-2" /> Refresh</Button>
      </div>

      <div className="flex gap-2">
        <button onClick={() => setView('wallets')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${view === 'wallets' ? 'bg-blue-600 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>Client Wallets</button>
        <button onClick={() => setView('withdrawals')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${view === 'withdrawals' ? 'bg-blue-600 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>Withdrawal Requests</button>
      </div>

      {loading && <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>}

      {view === 'wallets' && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="p-3 border-b border-slate-100">
            <div className="relative max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search client..." className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Client</th>
                  <th className="text-right px-4 py-2">Balance</th>
                  <th className="text-right px-4 py-2">Available</th>
                  <th className="text-right px-4 py-2">Min W/D</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredWallets.length === 0 && !loading && (
                  <tr><td colSpan="5" className="text-center py-8 text-slate-400">No wallets yet. Credit profit to create one.</td></tr>
                )}
                {filteredWallets.map((w) => (
                  <tr key={w.userId} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-800">{w.name || 'Unknown'}</p>
                      <p className="text-xs text-slate-400">{w.email} {w.clientId ? `\u2022 #${w.clientId}` : ''}</p>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-800">{inr(w.balance)}</td>
                    <td className="px-4 py-3 text-right text-green-600 font-semibold">{inr(w.available)}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{inr(w.minWithdrawal)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button size="sm" onClick={() => { setCreditFor(w); setCreditType('credit'); }} className="bg-green-600 hover:bg-green-700 text-white h-8 mr-1"><Plus className="w-3 h-3 mr-1" /> Profit</Button>
                      <Button size="sm" variant="outline" onClick={() => { setMinFor(w); setMinAmt(String(w.minWithdrawal || 500)); }} className="h-8"><Settings2 className="w-3 h-3 mr-1" /> Min</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === 'withdrawals' && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="p-3 border-b border-slate-100 flex gap-2 flex-wrap">
            {['all', 'Pending', 'Approved', 'Paid', 'Rejected'].map((s) => (
              <button key={s} onClick={() => setWStatus(s)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${wStatus === s ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}>{s}</button>
            ))}
          </div>
          <div className="divide-y divide-slate-100">
            {withdrawals.length === 0 && !loading && <div className="text-center py-8 text-slate-400">No withdrawal requests.</div>}
            {withdrawals.map((w) => (
              <div key={w.id} className="px-4 py-3 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-semibold text-slate-800">{inr(w.amount)} <span className="text-xs text-slate-400">({w.id})</span></p>
                  <p className="text-xs text-slate-500">{w.name} \u2022 {w.email} {w.clientId ? `\u2022 #${w.clientId}` : ''}</p>
                  <p className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(w.requestedAt).toLocaleString()} \u2022 {w.method}: {w.payoutDetails}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusStyle(w.status)}`}>{w.status}</span>
                  {w.status === 'Pending' && (
                    <>
                      <Button size="sm" onClick={() => updateWithdrawal(w.id, 'Approved')} className="bg-blue-600 hover:bg-blue-700 text-white h-8"><Check className="w-3 h-3 mr-1" /> Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => updateWithdrawal(w.id, 'Rejected')} className="h-8 text-red-600 border-red-200"><X className="w-3 h-3 mr-1" /> Reject</Button>
                    </>
                  )}
                  {w.status === 'Approved' && (
                    <Button size="sm" onClick={() => updateWithdrawal(w.id, 'Paid')} className="bg-green-600 hover:bg-green-700 text-white h-8">Mark Paid</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Credit modal */}
      {creditFor && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setCreditFor(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <button onClick={() => setCreditFor(null)} className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            <h3 className="text-lg font-bold text-slate-800 mb-1">Adjust Wallet</h3>
            <p className="text-sm text-slate-500 mb-4">{creditFor.name} \u2022 Balance {inr(creditFor.balance)}</p>
            <div className="space-y-3">
              <div className="flex gap-2">
                <button onClick={() => setCreditType('credit')} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${creditType === 'credit' ? 'bg-green-600 text-white' : 'bg-slate-100 text-slate-600'}`}>Credit Profit</button>
                <button onClick={() => setCreditType('debit')} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${creditType === 'debit' ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600'}`}>Debit</button>
              </div>
              <input type="number" value={creditAmt} onChange={(e) => setCreditAmt(e.target.value)} placeholder="Amount (\u20b9)" data-testid="admin-credit-amount" className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-slate-900" />
              <input value={creditNote} onChange={(e) => setCreditNote(e.target.value)} placeholder="Note (optional)" className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-slate-900" />
              <Button onClick={submitCredit} disabled={busy} data-testid="admin-credit-submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Min withdrawal modal */}
      {minFor && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMinFor(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <button onClick={() => setMinFor(null)} className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            <h3 className="text-lg font-bold text-slate-800 mb-1">Min Withdrawal</h3>
            <p className="text-sm text-slate-500 mb-4">{minFor.name}</p>
            <input type="number" value={minAmt} onChange={(e) => setMinAmt(e.target.value)} placeholder="Minimum amount (\u20b9)" data-testid="admin-min-amount" className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-slate-900 mb-3" />
            <Button onClick={submitMin} disabled={busy} data-testid="admin-min-submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletManagement;
